import { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import _ from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { empty, errorResponse, prepareResponseData } from "../../Utilities/utils";
import { Toast } from "primereact/toast";

// css
import "../students/Students.css";

// api
import subjectApi from "../../api/Subject";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import FullPageLoader from "../../components/loader/FullPageLoader";
import InputField from "../../components/form/InputField";
import { AuthContext } from "../Root/ProtectedRoute";

const required = "This field is required!";
const validationSchema = Yup.object().shape({
  title: Yup.string().required(required),
});

const AddUpdateSubjectActivities = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const [initialValues, setInitialValues] = useState({
    title: "",
  });
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { subjectActivityId } = useParams();
  const toastTR = useRef(null);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  useEffect(() => {
    if (subjectActivityId) {
      getSubjectActivityDetails();
    }
  }, [subjectActivityId]);

  const getSubjectActivityDetails = async () => {
    setIsLoading(true);
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await subjectApi.getSingleSubjectActivity(subjectActivityId, schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Something went wrong!"
            : "Failed to get class details."
        );
      }

      if (!empty(response_data) && !empty(response_data.response)) {
        const data = response_data.response;
        setInitialValues({
          title: !empty(data.title) ? data.title : "",
        });
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      if (empty(values)) {
        return responseDialog(
          "error",
          "Error Alert",
          "Something went wrong! Try again later."
        );
      }
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const title = !empty(values) && !empty(values.title) ? values.title : "";
      let response;
      let subjectActivityAdded = false;
      let subjectActivityUpdated = false;
      if (!subjectActivityId) {
        response = await subjectApi.addSubjectActivity(schoolId, title);
        subjectActivityAdded = true;
      } else {
        response = await subjectApi.updateSubjectActivity(
          subjectActivityId,
          schoolId,
          title
        );
        subjectActivityUpdated = true;
      }
      const response_data = prepareResponseData(response);
      if (empty(response_data.success)) {
        if (
          !empty(response_data) &&
          !empty(response_data.statusCodeType) &&
          response_data.statusCodeType === "unauthorized"
        ) {
          return navigate("/");
        }
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Something went wrong!"
        );
      }

      navigate("/subject-activities", {
        state: { subjectActivityAdded, subjectActivityUpdated },
      });
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="container flex-center-top">
            <MainHeader
              title={
                !empty(subjectActivityId)
                  ? "Update Subject Activity"
                  : "Add Subject Activity"
              }
            />
            <div className="form-container mt-30">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                <Form style={{ width: "100%" }}>
                  <div
                    style={{ width: "100%", display: "flex", columnGap: 12 }}
                  >
                    <InputField
                      placeholder="Enter Subject Activity Title E.g Lab, Practical, Outdoor Exercise etc"
                      name="title"
                      height={50}
                      labelTitle="Title"
                      required={true}
                    />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <ButtonIcon
                      height={45}
                      marginTop={5}
                      color="#ffffff"
                      backgroundColor="#633ccd"
                      width={300}
                      borderColor="#633ccd"
                      buttonText={
                        subjectActivityId ? "Update Subject Activity" : "Add Subject Activity"
                      }
                      type="submit"
                    />
                  </div>
                </Form>
              </Formik>
            </div>
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}
        <Toast ref={toastTR} position="bottom-left" />
      </AppWrapper>
    </>
  );
};

export default AddUpdateSubjectActivities;
